import { Switch as AntdSwitch } from 'antd';
import type { ComponentProps, ReactNode } from 'react';
import { useId } from 'react';

import styles from './switch.module.scss';

type AntdSwitchProps = Omit<ComponentProps<typeof AntdSwitch>, 'size'>;

export type SwitchSize = 'sm' | 'md' | 'lg';

export type SwitchProps = AntdSwitchProps & {
  /** Figma sizes: sm 32×16, md 40×20, lg 48×24. */
  size?: SwitchSize;
  /** Optional trailing label (Figma gap 16px, font 12/14/16px). */
  label?: ReactNode;
};

const LABEL_SIZE_CLASS: Record<SwitchSize, string> = {
  sm: styles.labelSm,
  md: styles.labelMd,
  lg: styles.labelLg,
};

export function Switch({ size = 'md', label, className, disabled, ...rest }: SwitchProps) {
  const labelId = useId();
  const control = (
    <AntdSwitch
      {...rest}
      disabled={disabled}
      size={size === 'sm' ? 'small' : 'default'}
      aria-labelledby={label ? labelId : undefined}
      className={[styles.switch, size === 'lg' ? styles.lg : undefined, className]
        .filter(Boolean)
        .join(' ')}
    />
  );
  if (!label) {
    return control;
  }
  return (
    <span className={styles.wrapper}>
      {control}
      <span
        id={labelId}
        className={[disabled ? styles.labelDisabled : styles.label, LABEL_SIZE_CLASS[size]].join(
          ' ',
        )}
      >
        {label}
      </span>
    </span>
  );
}
